/**
 * Admin "Benutzer" section — searchable table of all workspace users.
 *
 * Features:
 * - Lists all users from GET /api/admin/users (admin-only)
 * - Search/filter by name or email + type filter (Alle / Interne / Gäste)
 * - Per-row "Admin" and "Aktiv" toggles via PATCH /api/admin/users/:id
 * - Click on a row expands the UserAccessPanel: departments, spaces, guest
 *   invites (resend/revoke) plus rename and guest deletion
 * - Success toast on toggle; error toast with server guard message on 400
 * - Self-row protection: both toggles are disabled for the current user (isSelf)
 *   to mirror the server-side self-lockout guard, with a "(du)" hint
 */

import { Fragment } from "preact";
import { useEffect, useState } from "preact/hooks";
import { ChevronDown, ChevronRight } from "lucide-preact";
import { UserAvatar } from "./UserAvatar";
import { UserAccessPanel } from "./admin/UserAccessPanel";
import { toastError, toastSuccess } from "../lib/toast";
import { listAdminUsers, updateAdminUser, type AdminUser } from "../lib/api";
import { filterUsers, formatLastLogin, userDisplayName, userBadges, type UserBadge } from "../lib/adminUsers";
import { useMe } from "../lib/auth";
import { ApiError } from "../lib/api";

// ---------- badge styling ----------

const BADGE_CLS: Record<UserBadge, string> = {
  // Bot: tertiary — dieselbe Farbe wie das BotBadge im restlichen UI.
  bot:
    "inline-block rounded px-1.5 py-0.5 text-label font-medium uppercase tracking-wide bg-[color:var(--color-tertiary-container)] text-[color:var(--color-on-tertiary-container)]",
  admin:
    "inline-block rounded px-1.5 py-0.5 text-label font-medium uppercase tracking-wide bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]",
  extern:
    "inline-block rounded px-1.5 py-0.5 text-label font-medium uppercase tracking-wide bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]",
  inaktiv:
    "inline-block rounded px-1.5 py-0.5 text-label font-medium uppercase tracking-wide bg-[color:var(--color-error-container)] text-[color:var(--color-on-error-container)]",
};

const BADGE_LABEL: Record<UserBadge, string> = {
  bot: "Bot",
  admin: "Admin",
  extern: "Extern",
  inaktiv: "Inaktiv",
};

// ---------- toggle switch ----------

interface ToggleProps {
  checked: boolean;
  disabled: boolean;
  loading: boolean;
  label: string;
  onChange: (next: boolean) => void;
}

function Toggle({ checked, disabled, loading, label, onChange }: ToggleProps): preact.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled || loading}
      onClick={() => onChange(!checked)}
      class={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 ${
        disabled || loading
          ? "cursor-not-allowed opacity-40"
          : "cursor-pointer"
      } ${
        checked
          ? "bg-[color:var(--color-primary)]"
          : "bg-[color:var(--color-surface-container-high)]"
      }`}
    >
      <span
        aria-hidden="true"
        class={`pointer-events-none inline-block h-4 w-4 rounded-full border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] transition-transform ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

// ---------- main section ----------

type TypeFilter = "all" | "internal" | "external";

const TYPE_FILTERS: { id: TypeFilter; label: string }[] = [
  { id: "all", label: "Alle" },
  { id: "internal", label: "Interne" },
  { id: "external", label: "Gäste" },
];

export function UsersSection(): preact.JSX.Element {
  const me = useMe();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  // Aufgeklappte Zeile (Zugriffs-Detail-Panel); höchstens eine gleichzeitig.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Track per-row pending state: Set of user ids currently being updated
  const [pending, setPending] = useState<Set<number>>(new Set());

  useEffect(() => {
    void load();
  }, []);

  async function load(): Promise<void> {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listAdminUsers();
      setUsers(data);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(
    user: AdminUser,
    field: "is_admin" | "is_active",
    next: boolean
  ): Promise<void> {
    setPending((prev) => {
      const s = new Set(prev);
      s.add(user.id);
      return s;
    });
    // Optimistic update
    setUsers((prev) =>
      prev.map((u) => (u.id === user.id ? { ...u, [field]: next } : u))
    );
    try {
      const updated = await updateAdminUser(user.id, { [field]: next });
      // Sync with server response
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      const label = field === "is_admin" ? "Admin-Status" : "Aktiv-Status";
      toastSuccess(`${userDisplayName(updated)}: ${label} aktualisiert.`);
    } catch (err) {
      // Revert optimistic update
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, [field]: !next } : u))
      );
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
          ? err.message
          : "Fehler beim Aktualisieren";
      toastError(msg);
    } finally {
      setPending((prev) => {
        const s = new Set(prev);
        s.delete(user.id);
        return s;
      });
    }
  }

  if (loading) {
    return (
      <p class="text-sm text-[color:var(--color-on-surface-variant)]">
        Lädt Benutzer…
      </p>
    );
  }

  if (loadError) {
    return (
      <div class="space-y-2">
        <p class="text-sm text-[color:var(--color-error)]">
          Fehler: {loadError}
        </p>
        <button
          type="button"
          onClick={() => void load()}
          class="text-sm text-[color:var(--color-primary)] hover:underline"
        >
          Erneut laden
        </button>
      </div>
    );
  }

  // Owner-Namen für Bot-Zeilen: aus derselben Liste aufgelöst, der
  // Users-Store (/api/users) wird hier nicht geladen.
  const nameById = new Map(users.map((u) => [u.id, userDisplayName(u)]));

  const filtered = filterUsers(users, query).filter((u) =>
    typeFilter === "all"
      ? true
      : typeFilter === "external"
        ? u.is_external
        : !u.is_external
  );

  return (
    <section class="space-y-4">
      {/* Search + type filter */}
      <div class="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={query}
          onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          placeholder="Suche nach Name oder E-Mail…"
          class="flex-1 max-w-sm rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          aria-label="Benutzer suchen"
        />
        <div class="flex items-center gap-1" role="group" aria-label="Benutzertyp filtern">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setTypeFilter(f.id)}
              aria-pressed={typeFilter === f.id}
              class={`rounded-full px-2.5 py-1 text-xs transition-colors ${
                typeFilter === f.id
                  ? "bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)] font-medium"
                  : "bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span class="text-xs text-[color:var(--color-on-surface-variant)]">
          {filtered.length} von {users.length}
        </span>
      </div>

      {/* Table */}
      <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-x-auto">
        {filtered.length === 0 ? (
          <p class="p-4 text-sm text-[color:var(--color-on-surface-variant)] italic">
            {query ? "Kein Benutzer gefunden." : "Noch keine Benutzer."}
          </p>
        ) : (
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="border-b border-[color:var(--color-outline-variant)]">
                <th
                  scope="col"
                  class="px-4 py-2.5 text-left text-xs font-medium text-[color:var(--color-on-surface-variant)] uppercase tracking-wide"
                >
                  Benutzer
                </th>
                <th
                  scope="col"
                  class="px-4 py-2.5 text-left text-xs font-medium text-[color:var(--color-on-surface-variant)] uppercase tracking-wide hidden sm:table-cell"
                >
                  Letzter Login
                </th>
                <th
                  scope="col"
                  class="px-4 py-2.5 text-center text-xs font-medium text-[color:var(--color-on-surface-variant)] uppercase tracking-wide"
                >
                  Admin
                </th>
                <th
                  scope="col"
                  class="px-4 py-2.5 text-center text-xs font-medium text-[color:var(--color-on-surface-variant)] uppercase tracking-wide"
                >
                  Aktiv
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-[color:var(--color-outline-variant)]">
              {filtered.map((user) => {
                const isSelf = me.user?.id === user.id;
                const isPending = pending.has(user.id);
                const badges = userBadges(user);
                const displayName = userDisplayName(user);
                const isExpanded = expandedId === user.id;

                return (
                  <Fragment key={user.id}>
                    <tr
                      onClick={() =>
                        setExpandedId(isExpanded ? null : user.id)
                      }
                      class={`cursor-pointer transition-colors ${
                        isSelf
                          ? "bg-[color:var(--color-surface-container-low)]"
                          : "hover:bg-[color:var(--color-surface-container-lowest)]"
                      }`}
                    >
                      {/* User cell: chevron + avatar + name + email + badges */}
                      <td class="px-4 py-3">
                        <div class="flex items-center gap-2.5">
                          <span
                            class="text-[color:var(--color-on-surface-variant)]"
                            aria-hidden="true"
                          >
                            {isExpanded ? (
                              <ChevronDown size={14} />
                            ) : (
                              <ChevronRight size={14} />
                            )}
                          </span>
                          <UserAvatar
                            userId={user.id}
                            name={displayName}
                            size={32}
                          />
                          <div class="min-w-0">
                            <div class="flex items-center gap-1.5 flex-wrap">
                              <span class="font-medium text-[color:var(--color-on-surface)] truncate">
                                {displayName}
                              </span>
                              {isSelf && (
                                <span
                                  class="text-label text-[color:var(--color-on-surface-variant)]"
                                  aria-label="Das bist du"
                                >
                                  (du)
                                </span>
                              )}
                              {badges.map((badge) => (
                                <span key={badge} class={BADGE_CLS[badge]}>
                                  {BADGE_LABEL[badge]}
                                </span>
                              ))}
                            </div>
                            <div class="text-xs text-[color:var(--color-on-surface-variant)] truncate mt-0.5">
                              {user.email}
                            </div>
                            {user.is_bot && user.bot_owner_id != null && (
                              <div class="text-label text-[color:var(--color-on-surface-variant)] truncate">
                                im Auftrag von{" "}
                                {nameById.get(user.bot_owner_id) ??
                                  `#${user.bot_owner_id}`}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Last login */}
                      <td class="px-4 py-3 text-xs text-[color:var(--color-on-surface-variant)] hidden sm:table-cell whitespace-nowrap">
                        {formatLastLogin(user.last_login_at)}
                      </td>

                      {/* Admin toggle — Klicks nicht zum Zeilen-Expand durchreichen */}
                      <td
                        class="px-4 py-3 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div class="flex items-center justify-center gap-1">
                          {/* Bots dürfen laut DB-CHECK nie Admin sein — der
                              Toggle bliebe sonst ein garantierter Fehler. */}
                          <Toggle
                            checked={user.is_admin}
                            disabled={isSelf || user.is_bot === true}
                            loading={isPending}
                            label={`Admin-Status für ${displayName}`}
                            onChange={(next) =>
                              void handleToggle(user, "is_admin", next)
                            }
                          />
                        </div>
                      </td>

                      {/* Active toggle */}
                      <td
                        class="px-4 py-3 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div class="flex items-center justify-center gap-1">
                          <Toggle
                            checked={user.is_active}
                            disabled={isSelf}
                            loading={isPending}
                            label={`Aktiv-Status für ${displayName}`}
                            onChange={(next) =>
                              void handleToggle(user, "is_active", next)
                            }
                          />
                        </div>
                      </td>
                    </tr>

                    {/* Detail-Panel: Abteilungen, Spaces, Gast-Zugriffe, Aktionen */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={4} class="p-0">
                          <UserAccessPanel
                            user={user}
                            isSelf={isSelf}
                            onUserChanged={(updated) =>
                              setUsers((prev) =>
                                prev.map((u) =>
                                  u.id === updated.id ? updated : u
                                )
                              )
                            }
                            onUserDeleted={(id) => {
                              setUsers((prev) =>
                                prev.filter((u) => u.id !== id)
                              );
                              setExpandedId(null);
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p class="text-xs text-[color:var(--color-on-surface-variant)]">
        Zeile anklicken für Details: Abteilungen, Spaces, Gast-Zugriffe sowie
        Umbenennen, Zugangslink-Neuversand und Löschen (nur Gäste). Interne
        Benutzer können nicht gelöscht werden — deaktiviere den Account um den
        Zugriff zu entziehen. Eigene Admin- oder Aktiv-Status-Änderungen sind
        deaktiviert um Selbst-Aussperrung zu verhindern.
      </p>
    </section>
  );
}
